import { Injectable } from '@angular/core';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { OrganizationUser } from '../../types/organization-user.type';
import { BenchmarkingSettingsModel, DebuggingSettingsModel, GeneralSettingsModel, ProcessesCostSettingsModel, ReportingSettingsModel, Service, ImportExportPackage } from '../../types/organization.type';
import { BaseApiService } from './base-api.service';
import { ResetPasswordResult } from '../../types/set-password-result.type';
import { firstValueFrom } from 'rxjs';
import { AnomalyDetectionConfiguration } from '../../types/generated.types';

@Injectable({ providedIn: 'root' })
export class OrganizationSettingsApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/organization-settings`;
  }

  async getBenchmarkingSettings(): Promise<BenchmarkingSettingsModel> {
    const result = await this.get<BenchmarkingSettingsModel>('benchmarking');
    return result;
  }

  async updateBenchmarkingSettings(model: BenchmarkingSettingsModel): Promise<void> {
    await this.patch('benchmarking', model);
  }

  async getServicesSettings(): Promise<Service[]> {
    const result = await this.get<Service[]>('services');
    return result;
  }

  async updateServicesSettings(services: Service[]): Promise<void> {
    await this.patch('services', services);
  }

  async getGeneralSettings(): Promise<GeneralSettingsModel> {
    const result = await this.get<GeneralSettingsModel>('general');
    ApiConversionHelper.convertTimeSpans(result, ['businessHoursFrom', 'businessHoursTo']);
    return result;
  }

  async updateGeneralSettings(model: GeneralSettingsModel): Promise<void> {
    ApiConversionHelper.convertTimeForApi(model, ['businessHoursFrom', 'businessHoursTo']);
    await this.patch('general', model);
  }

  async getDebuggingSettings(): Promise<DebuggingSettingsModel> {
    const result = await this.get<DebuggingSettingsModel>('debugging');
    return result;
  }

  async updateDebuggingSettings(model: DebuggingSettingsModel): Promise<void> {
    await this.patch('debugging', model);
  }

  async getProcessesCostSettings(): Promise<ProcessesCostSettingsModel> {
    const result = await this.get<ProcessesCostSettingsModel>('processes-cost');
    return result;
  }

  async updateProcessesCostSettings(model: ProcessesCostSettingsModel): Promise<void> {
    await this.patch('processes-cost', model);
  }

  async getReportingSettings(): Promise<ReportingSettingsModel> {
    return await this.get<ReportingSettingsModel>('reporting');
  }

  async updateReportingSettings(model: ReportingSettingsModel): Promise<void> {
    await this.patch('reporting', model);
  }

  async getAnomalyDetectionSettings(): Promise<AnomalyDetectionConfiguration> {
    return await this.get<AnomalyDetectionConfiguration>('anomaly-detection');
  }

  async updateAnomalyDetectionSettings(model: AnomalyDetectionConfiguration): Promise<void> {
    await this.patch('anomaly-detection', model);
  }

  getOrganizationLogo(): Promise<Blob> {
    return this.getBlob('logo');
  }

  async uploadOrganizationLogo(file: File): Promise<void> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    const url = this.getUrl('logo');
    const options = { headers: { 'Content-Type': 'multipart/form-data' } };
    await firstValueFrom(this.httpClient.post(url, formData, options));
  }

  deleteOrganizationLogo(): Promise<void> {
    return this.delete('logo');
  }

  async getOrganizationUsers(): Promise<OrganizationUser[]> {
    const result = await this.get<OrganizationUser[]>('users');
    ApiConversionHelper.convertDates(result, ['lastLogin']);
    return result;
  }

  async createOrganizationUser(user: OrganizationUser): Promise<ResetPasswordResult> {
    return await this.postWithResult('users', user);
  }

  async updateOrganizationUser(user: OrganizationUser): Promise<void> {
    return await this.put(`users/${user.id}`, user);
  }

  async deleteOrganizationUser(id: string): Promise<void> {
    return await this.delete(`users/${id}`);
  }

  async resetOrganizationUserPassword(id: string): Promise<ResetPasswordResult> {
    return await this.postWithResult(`users/${id}/password-reset`, null);
  }

  async exportBusinessSettings(): Promise<ImportExportPackage> {
    return await this.get<ImportExportPackage>('business-settings/export');
  }

  async importBusinessSettings(importPackage: ImportExportPackage): Promise<void> {
    await this.post('business-settings/import', importPackage);
  }
}
